import {Assert, CE} from "js-vextensions";
import {AssertNotify, RunInAction} from "web-vcore";
import {observable} from "mobx";

export class MemoryPalaceFrame {
	constructor(data: Partial<MemoryPalaceFrame>) { Object.assign(this, data); }
	id: string; // used as the store's key-path
	createdAt: number; // timestamp for sorting
	imageData: string; // base64 encoded PNG data from canvas
	target: string; // eg. name of character mentally "imprinted" into the given frame/image
}

export const MemoryPalaceFrameDB_maxFramesPerTarget = 4; // (1 for the entry just stored, plus 3 for the previous ones shown in UI)
export class MemoryPalaceFrameDB {
	private onInitHandlers = [] as (()=>void)[];
	EnsureInitDone() {
		return new Promise<void>((resolve, reject)=>{
			if (this.db) resolve();
			else this.onInitHandlers.push(resolve);
		});
	}

	private db: IDBDatabase;
	async Init() {
		return new Promise<void>((resolve, reject)=>{
			const request = indexedDB.open("MemoryPalaceFrames", 1);
			request.onupgradeneeded = event=>{
				const db = (event.target as IDBOpenDBRequest).result;
				const store = db.createObjectStore("frames", {keyPath: "id"});
				store.createIndex("createdAt", "createdAt", {unique: false});
				store.createIndex("target", "target", {unique: false});
			};
			request.onerror = ()=>reject(request.error);
			request.onsuccess = ()=>{
				this.db = request.result;
				this.onInitHandlers.forEach(handler=>handler());
				resolve();
			};
		});
	}

	// Store a frame and enforce max X per target
	async StoreFrame(frame_partial: Omit<MemoryPalaceFrame, "id" | "createdAt">) {
		await this.EnsureInitDone();

		AssertNotify(frame_partial.target != null && frame_partial.imageData != null, "Frame must have target and imageData.");
		Assert(frame_partial.imageData.length > "data:,".length, "Image data must be non-empty.");

		const frame_final: MemoryPalaceFrame = {
			id: `${frame_partial.target}:${Date.now()}:${Math.random()}`,
			createdAt: Date.now(),
			...frame_partial,
		};

		const transaction = this.db.transaction(["frames"], "readwrite");
		const store = transaction.objectStore("frames");

		// Store the new frame
		await new Promise<void>((resolve, reject)=>{
			const request = store.put(frame_final);
			request.onsuccess = ()=>resolve();
			request.onerror = ()=>reject(request.error);
		});

		// Clean up old frames for this target (keep only X most recent)
		await this.CleanupOldFramesForTarget(frame_partial.target);

		this.NotifyDataChangedForTarget(frame_partial.target);
	}

	// Delete oldest frames for a target, keeping only the X most recent
	private async CleanupOldFramesForTarget(target: string): Promise<void> {
		await this.EnsureInitDone();
		const frames = await this.GetFramesForTarget(target);

		if (frames.length > MemoryPalaceFrameDB_maxFramesPerTarget) {
			// Sort by createdAt descending and get frames to delete
			frames.sort((a, b)=>b.createdAt - a.createdAt);
			const framesToDelete = frames.slice(MemoryPalaceFrameDB_maxFramesPerTarget);

			const transaction = this.db.transaction(["frames"], "readwrite");
			const store = transaction.objectStore("frames");

			for (const frame of framesToDelete) {
				await new Promise<void>((resolve, reject)=>{
					const request = store.delete(frame.id);
					request.onsuccess = ()=>resolve();
					request.onerror = ()=>reject(request.error);
				});
			}
		}
	}

	async DeleteFramesForTarget(target: string): Promise<MemoryPalaceFrame[]> {
		await this.EnsureInitDone();
		const transaction = this.db.transaction(["frames"], "readwrite");
		const store = transaction.objectStore("frames");
		const index = store.index("target");

		return new Promise<MemoryPalaceFrame[]>((resolve, reject)=>{
			const request = index.openCursor(IDBKeyRange.only(target));
			const entriesDeleted = [] as MemoryPalaceFrame[];
			request.onsuccess = event=>{
				const cursor = (event.target as IDBRequest).result;
				if (cursor) {
					const frame = cursor.value as MemoryPalaceFrame;
					cursor.delete();
					cursor.continue();
					entriesDeleted.push(frame);
				} else {
					this.NotifyDataChangedForTarget(target);
					resolve(entriesDeleted);
				}
			};
			request.onerror = ()=>reject(request.error);
		});
	}

	async ClearAllData(): Promise<void> {
		await this.EnsureInitDone();
		const transaction = this.db.transaction(["frames"], "readwrite");
		const store = transaction.objectStore("frames");

		return new Promise((resolve, reject)=>{
			const request = store.clear();
			request.onsuccess = ()=>{
				RunInAction("MemoryPalaceFrameDB.ClearAllData", ()=>{
					this.mobxData.targetFrames_retrievalsInProgress.clear();
					this.mobxData.targetFrames.clear();
				});
				resolve();
			};
			request.onerror = ()=>reject(request.error);
		});
	}

	// Notify observers that data has changed
	NotifyDataChangedForTarget(target: string) {
		RunInAction("MemoryPalaceFrameDB.NotifyDataChangedForTarget", ()=>{
			this.mobxData.targetFrames_retrievalsInProgress.delete(target);
			this.mobxData.targetFrames.delete(target);
		});
	}

	mobxData = {
		targetFrames_retrievalsInProgress: new Map<string, boolean>(),
		targetFrames: observable.map<string, MemoryPalaceFrame[]>(),
	};

	// Get all frames for a specific target (sorted by createdAt descending)
	async GetFramesForTarget(target: string): Promise<MemoryPalaceFrame[]> {
		await this.EnsureInitDone();
		const transaction = this.db.transaction(["frames"], "readonly");
		const store = transaction.objectStore("frames");
		const index = store.index("target");

		return new Promise((resolve, reject)=>{
			const frames = [] as MemoryPalaceFrame[];
			const request = index.openCursor(IDBKeyRange.only(target));
			request.onsuccess = event=>{
				const cursor = (event.target as IDBRequest).result;
				if (cursor) {
					frames.push(cursor.value as MemoryPalaceFrame);
					cursor.continue();
				} else {
					// Sort by createdAt descending (most recent first)
					frames.sort((a, b)=>b.createdAt - a.createdAt);
					resolve(frames);
				}
			};
			request.onerror = ()=>reject(request.error);
		});
	}

	GetFramesForTarget_MobX(target: string): MemoryPalaceFrame[] {
		if (!this.mobxData.targetFrames_retrievalsInProgress.get(target) && !this.mobxData.targetFrames.get(target)) {
			this.mobxData.targetFrames_retrievalsInProgress.set(target, true);
			(async()=>{
				try {
					const frames = await this.GetFramesForTarget(target);
					this.mobxData.targetFrames_retrievalsInProgress.set(target, false);
					RunInAction("MemoryPalaceFrameDB.GetFramesForTarget_MobX", ()=>this.mobxData.targetFrames.set(target, frames));
				} catch (err) {
					this.mobxData.targetFrames_retrievalsInProgress.set(target, false);
					console.error("Error retrieving frames for target:", target, err);
				}
			})();
		}
		return this.mobxData.targetFrames.get(target) ?? [];
	}
}

export const memoryPalaceFrameDB = new MemoryPalaceFrameDB();
memoryPalaceFrameDB.Init();